import React, { useState, useEffect, useMemo } from 'react';
import { currencyFormat } from 'simple-currency-format';
import { Box, Divider, Flex, Title, Card, Skeleton, SegmentedControl, Button, Badge, Text, Stack } from '@mantine/core';

import dayjs from 'dayjs';
import 'dayjs/locale/es-mx';
import { useLocation } from 'react-router-dom';
import { PiUserCircle, PiUsers, PiCurrencyDollar, PiWallet, PiEnvelope } from 'react-icons/pi';

import BoxSummary from '../components/Dashboard/BoxSummary';
import Record from '../components/Dashboard/Record';
import RowRenovation from '../components/Dashboard/RowRenovation';

import { useDashboardStore } from '../store/useDashboardStore';
import ConfirmRollback from '../components/Dashboard/ConfirmRollback';
import Banks from '../components/Dashboard/Banks';

const Dashboard = () =>
{
  const today = useMemo(() => dayjs().format('YYYY-MM-DD'), []);
  const tenDays = useMemo(() => dayjs().add(10, 'day').format('YYYY-MM-DD'), []);
  const location = useLocation();
  const [ value, setValue ] = useState('lastRenovation');


  const {
    itemsCustomers,
    itemsPostpago,
    itemsPrepago,
    totalPostpago,
    totalPrepago,
    totalAmount,
    records,
    renovation,
    loadingBanks,
    rollbackData,
    openRollBack,
    fetchDashboardData,
    toggleRollback,
    closeRollback,
    rollBackRenovation,
    sendingEmail,
    sendEmailReport
  } = useDashboardStore();

  useEffect(() =>
  {
    // Fetch dashboard data whenever the component mounts or when the location changes
    // This ensures data is refreshed every time the user visits this page
    if (location.pathname === '/dashboard') {
      fetchDashboardData();
    }
  }, [ location, fetchDashboardData ]);

  return (
    <Box className="w-full gap-4 px-2 h-[100%]">
      <Box className='flex flex-row'>
        <Box className='flex-grow'>
          <Box mb="xl">
            <Flex justify="space-between" align="center" gap="xs">
              <Flex align="baseline" gap="xs">
                <Title order={ 1 } fw={ 900 } style={ { letterSpacing: '-1px' } }>NOC DASHBOARD</Title>
                <Badge variant="dot" color="yellow" size="lg" radius="xs">
                  { dayjs().locale('es-mx').format('MMMM YYYY').toUpperCase() }
                </Badge>
              </Flex>
              <Button
                variant="filled"
                color="yellow"
                leftSection={ <PiEnvelope size={ 18 } /> }
                onClick={ sendEmailReport }
                loading={ sendingEmail }
                size="sm"
              >
                GENERAR REPORTE
              </Button>
            </Flex>
          </Box>

          <Box className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            { renderBoxSummaries({
              itemsCustomers,
              itemsPostpago,
              itemsPrepago,
              totalPostpago,
              totalPrepago,
              totalAmount,
            }) }
          </Box>
          <Divider py="md" />
          <Box className='block lg:hidden'>
            <Section title="Bancos">
              { loadingBanks ? renderSkeletons(6) : <Banks /> }
            </Section>
          </Box>
          <SegmentedControl
            fullWidth size="md" radius="md"
            my="md"
            value={ value }
            onChange={ setValue }
            data={ [
              { label: 'Últimas renovaciones', value: 'lastRenovation' },
              { label: 'Próximos a vencer', value: 'next' },

            ] }
          />
          <Box className="my-4 grid grid-cols-1">
            <Card padding={ 0 } radius="xs" withBorder shadow="sm">
              <Box p="md" style={ { backgroundColor: 'var(--mantine-color-gray-0)', borderBottom: '1px solid var(--mantine-color-gray-3)' } }>
                <Flex justify="space-between" align="center">
                  <Text size="xs" fw={ 900 } tt="uppercase" style={ { letterSpacing: '1px' } }>
                    { value === 'lastRenovation' ? 'Registro de Transacciones Recientes' : 'Cola de Vencimientos Críticos' }
                  </Text>
                  <Badge variant="outline" color="gray" size="xs" radius="xs">
                    Real-time updates
                  </Badge>
                </Flex>
              </Box>

              <Box p="xs">
                { value === 'lastRenovation' && <Stack gap="xs">
                  { records ? records.slice(0, 8).map((item) => (
                    <Record key={ item._id } item={ item } rollBackRenovation={ toggleRollback } />
                  )) : renderSkeletons(6) }
                </Stack> }

                { value === 'next' && <Stack gap="xs">
                  { renovation ? renovation.slice(0, 8).map((item) => (
                    <RowRenovation key={ item._id } item={ item } today={ today } tenDays={ tenDays } />
                  )) : renderSkeletons(6) }
                </Stack> }
              </Box>
            </Card>
          </Box>
        </Box>

      </Box>
      <ConfirmRollback show={ openRollBack } onHide={ closeRollback } item={ rollbackData } onConfirm={ rollBackRenovation } />
    </Box>
  );
};

const renderBoxSummaries = ({ itemsCustomers,
  itemsPostpago,
  itemsPrepago,
  totalPostpago,
  totalPrepago,
  totalAmount, }) =>
{
  const summaries = [
    { title: 'Usuario postpago', value: itemsPostpago, icon: <PiUserCircle size="18" /> },
    { title: 'Usuarios prepago', value: itemsPrepago, icon: <PiUserCircle size="18" /> },
    { title: 'Total usuarios', value: itemsCustomers, icon: <PiUsers size="18" /> },
    { title: 'Total Postpago', value: totalPostpago, icon: <PiCurrencyDollar size="18" />, currency: true },
    { title: 'Total Prepago', value: totalPrepago, icon: <PiCurrencyDollar size="18" />, currency: true },
    { title: 'Total de ingresos', value: totalAmount, icon: <PiWallet size="18" />, currency: true },
  ];
  return summaries.map((item, index) =>
    item.value !== undefined && item.value !== null ? (
      <BoxSummary
        key={ index }
        title={ item.title }
        icon={ item.icon }
        value={ item.currency ? formatCurrency(item.value) : item.value }
        color="gray"
      />
    ) : (
      <Skeleton key={ index } height={ 80 } radius="md" />
    )
  );
};

const renderSkeletons = (count) => Array.from({ length: count }, (_, i) => <Skeleton key={ i } height={ 80 } radius="md" my={ 4 } />);
const formatCurrency = (amount) => `${ currencyFormat(amount, 'es-MX', 'MXN', 2) } mxn`;
const Section = ({ title, children }) => (
  <Box>
    <Title order={ 4 }>{ title }</Title>
    <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">{ children }</section>
  </Box>
);

// const SectionBanks = ({ title, children }) => (
//   <Box className='w-full lg:w-64 mx-2 gap-2 hidden lg:block'>
//     <Title order={ 4 }>{ title }</Title>
//     <section className="grid grid-cols-1 gap-2">{ children }</section>
//   </Box>
// );

const CardSection = ({ title, data, children }) => (
  <Card shadow="sm" padding="xl" radius="lg" withBorder>

    { data
      ? data.map((item) => (
        <Box key={ item._id } py="xs" className='gap-2'>
          { children(item) }
        </Box>
      ))
      : renderSkeletons(6) }
  </Card>
);

export default Dashboard;
